import React, { useState, useEffect } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from '../config/api';
import { getTabsForRole } from '../config/dashboardConfig';
import MultiActionFAB from './fab/MultiActionFAB';
import './Dashboard.css';
import './AdminTheme.css';

// Import SVG icons
import logoIcon from '../assets/icons/logo.svg';
import overviewIcon from '../assets/icons/overview.svg';
import vehiclesIcon from '../assets/icons/vehicles.svg';
import customersIcon from '../assets/icons/customers.svg';
import transactionsIcon from '../assets/icons/transactions.svg';
import paymentsIcon from '../assets/icons/payments.svg';
import settingsIcon from '../assets/icons/settings.svg';
import reportIcon from '../assets/icons/report.svg';
import accountIcon from '../assets/icons/account.svg';
import rtoIcon from '../assets/icons/rto.svg';
import calendarIcon from '../assets/icons/calendar.svg';
import usersIcon from '../assets/icons/users.svg';
import tasksIcon from '../assets/icons/tasks.svg';

// Icon mapping
const icons = {
  logo: logoIcon,
  overview: overviewIcon,
  vehicles: vehiclesIcon,
  customers: customersIcon,
  transactions: transactionsIcon,
  payments: paymentsIcon,
  settings: settingsIcon,
  report: reportIcon,
  account: accountIcon,
  rto: rtoIcon,
  calendar: calendarIcon,
  users: usersIcon,
  tasks: tasksIcon,
};

/**
 * UnifiedDashboardLayout component serves as the main layout for both manager and admin dashboards
 * Dynamically renders tabs based on user role
 */
export function UnifiedDashboardLayout() {
  const { user, apiCall, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fullUser, setFullUser] = useState(user);
  const [transactions, setTransactions] = useState([]);
  const [payments, setPayments] = useState([]);

  // Sync fullUser when user changes (e.g., after login or token refresh)
  useEffect(() => {
    if (user && !fullUser) {
      setFullUser(user);
    }
  }, [user, fullUser]);

  // Refresh full user details on mount
  useEffect(() => {
    if (user && refreshUser) {
      refreshUser().then(result => {
        if (result.success && result.user) {
          setFullUser(result.user);
        }
      });
    }
  }, []); // Only run once on mount

  useEffect(() => {
    // Wait for fullUser to be loaded before fetching dashboard stats
    if (fullUser && apiCall) {
      loadDashboardStats();
    }
  }, [fullUser, apiCall]);

  // Load stats for the overview
  const loadDashboardStats = async () => {
    setLoading(true);
    try {
      let dashboardStatsRes = null;

      // Only fetch dashboard stats for managers (not admins)
      // Admins load their own data in DashboardOverview using the admin stores overview endpoint
      console.log('[UnifiedDashboardLayout] loadDashboardStats called', {
        fullUserRole: fullUser?.role,
        isManager: fullUser?.role === 'manager'
      });

      if (fullUser?.role === 'manager') {
        dashboardStatsRes = await apiCall(`${API_BASE_URL}/dashboard/stats`);
        console.log('[UnifiedDashboardLayout] Dashboard stats response:', dashboardStatsRes);
      }

      // Fetch totals for customers and payments
      const [customersRes, paymentsRes] = await Promise.all([
        apiCall(`${API_BASE_URL}/customers?limit=1`),
        apiCall(`${API_BASE_URL}/payments?limit=1`),
      ]);

      const statsToSet = {
        totalVehicles: dashboardStatsRes?.data?.totalVehicles || 0,
        availableVehicles: dashboardStatsRes?.data?.availableVehicles || 0,
        rentedVehicles: dashboardStatsRes?.data?.rentedVehicles || 0,
        maintenanceVehicles: dashboardStatsRes?.data?.maintenanceVehicles || 0,
        rtoVehicles: dashboardStatsRes?.data?.rtoVehicles || 0,
        totalTransactions: dashboardStatsRes?.data?.totalTransactions || 0,
        totalCustomers: customersRes?.total || 0,
        totalPayments: paymentsRes?.total || 0,
      };

      console.log('[UnifiedDashboardLayout] Setting stats:', statsToSet);
      setStats(statsToSet);

    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Deprecated - individual pages now load their own data
  const loadDashboardData = async () => {
    await loadDashboardStats();
  };

  const getActiveTab = () => {
    const path = location.pathname;
    // Extract tab from path: /dashboard/vehicles -> vehicles
    const match = path.match(/\/dashboard\/([^/]+)/);
    if (match) return match[1];
    
    // Check if it's just /dashboard
    if (path.includes('/dashboard')) return 'overview';
    return 'overview';
  };

  const handleTabClick = (tabPath) => {
    if (tabPath === '') {
      navigate('/dashboard');
    } else {
      navigate(`/dashboard/${tabPath}`);
    }
  };

  const tabs = getTabsForRole(user?.role);
  const activeTab = getActiveTab();
  const roleLabel = user?.role === 'admin' ? 'Admin'
    : user?.role === 'auditor' ? 'Auditor' : 'Manager';

  return (
    <div className="tg-dash tg-admin">
      <header className="tg-admin-header">
        <div className="tg-topbar">
          <div className="tg-topbar-row">
            <div className="tg-brand">
              <span className="tg-brand-mark">T</span>
              Tegrat Auto <span className="tg-brand-role">{roleLabel}</span>
            </div>
            <div className="tg-topbar-right">
              <span className="tg-user-chip">
                {user?.first_name || user?.email} · {user?.role}
              </span>
              <button type="button" className="tg-btn sm" onClick={logout}>
                Logout
              </button>
            </div>
          </div>
        </div>
        <nav className="tg-tabbar">
          <div className="tg-tabbar-track">
            {tabs.map(tab => {
              const isActive = activeTab === tab.id || (activeTab === '' && tab.id === 'overview');
              // Preserve the manager's domain colour-coding on the active pill;
              // tabs without a domain colour fall back to the tg citrus.
              const activeStyle = isActive && tab.domainColor
                ? { background: tab.domainColor, borderColor: tab.domainColor }
                : undefined;
              return (
                <button
                  key={tab.id}
                  type="button"
                  className={`tg-tab ${isActive ? 'active' : ''}`}
                  onClick={() => handleTabClick(tab.path)}
                  title={`Navigate to ${tab.label}`}
                  style={activeStyle}
                >
                  {tab.icon && icons[tab.icon] && <img src={icons[tab.icon]} alt="" />}
                  {tab.label}
                </button>
              );
            })}
          </div>
        </nav>
      </header>

      <main className="tg-admin-main">
        <Outlet context={{
          stats,
          loading,
          transactions,
          setTransactions,
          payments,
          setPayments,
          loadDashboardData,
          apiCall,
          user: fullUser || user,
          role: fullUser?.role || user?.role,
        }} />
      </main>

      {/* Multi-Action FAB for quick actions (task, transaction, payment) */}
      <MultiActionFAB onActionComplete={loadDashboardData} />
    </div>
  );
}

export default UnifiedDashboardLayout;
